import React from "react"
import StatsTableRow from "./StatsTableRow"
import { WeekStats, DayStats } from "../types"

interface WeeklyStatsTableProps {
  statsData: WeekStats[]
  allStatesSet: Set<string>
  onTotalClick: (state: string, week: WeekStats) => Promise<void>
  onTransitionClick: (type: "incoming" | "outgoing", state: string, week: WeekStats) => void
  getStateDisplayName: (state: string) => string
  getStateColor: (state: string) => string
}

const WeeklyStatsTable: React.FC<WeeklyStatsTableProps> = ({
  statsData,
  allStatesSet,
  onTotalClick,
  onTransitionClick,
  getStateDisplayName,
  getStateColor,
}) => {
  const userSideStates = [
    "invite_new",
    "waiting_address",
    "address_rejected",
    "waiting_content",
    "content_rejected",
    "ugc_rejected",
    "content_approved",
  ]
  const companySideStates = [
    "invite_success",
    "first_application",
    "address_to_approve",
    "content_to_share",
    "content_shared",
    "ugc_content",
  ]

  const hasBrandData = (
    week: WeekStats,
    state: string,
    type: "stateCounts" | "incoming" | "outgoing",
  ) => {
    if (type === "stateCounts") {
      return week.days.some((d: DayStats) =>
        (d.stateCounts || []).some((s) => s.state === state && s.brands && s.brands.length > 0),
      )
    } else if (type === "incoming") {
      return week.days.some((day: DayStats) =>
        (day.stateTransitions || []).some((t) => t.to === state && t.brands && t.brands.length > 0),
      )
    } else if (type === "outgoing") {
      return week.days.some((day: DayStats) =>
        (day.stateTransitions || []).some(
          (t) => t.from === state && t.brands && t.brands.length > 0,
        ),
      )
    }
    return false
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100">
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-500 rounded-lg p-2">
            <span className="text-white text-lg">📅</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Haftalık Durum İstatistikleri</h2>
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto relative" style={{ maxHeight: "100vh" }}>
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-1 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                Durum
              </th>
              {statsData.map((week) => {
                const startDate = new Date(week.startDate)
                const endDate = new Date(week.endDate)
                const formatDate = (date: Date) => {
                  const day = date.getDate().toString().padStart(2, "0")
                  const month = (date.getMonth() + 1).toString().padStart(2, "0")
                  return `${day}/${month}`
                }
                return (
                  <th
                    key={`${week.year}-${week.weekNumber}`}
                    colSpan={4}
                    className="px-0 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200 bg-gray-50"
                  >
                    {formatDate(startDate)} - {formatDate(endDate)}
                  </th>
                )
              })}
            </tr>
            <tr>
              <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200"></th>
              {statsData.map((week) => (
                <React.Fragment key={`${week.year}-${week.weekNumber}`}>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200 bg-gray-50">
                    Toplam Başvuru
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    State&apos;e Geçen
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    State&apos;den Çıkan
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Değişim
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {userSideStates.some((state) => allStatesSet.has(state)) && (
              <tr>
                <td
                  className="bg-blue-50 text-left px-1 py-3 text-sm font-bold text-blue-800 uppercase tracking-wider border-b-2 border-blue-200 sticky left-0 z-5 bg-blue-50 border-r border-blue-200"
                  style={{ minWidth: "120px", width: "120px" }}
                >
                  Kullanıcı Tarafı
                </td>
                <td
                  colSpan={statsData.length * 4}
                  className="bg-blue-50 border-b-2 border-blue-200"
                ></td>
              </tr>
            )}
            {userSideStates
              .filter((state) => allStatesSet.has(state))
              .map((state) => (
                <StatsTableRow
                  key={state}
                  state={state}
                  statsData={statsData}
                  viewMode="weekly"
                  onTotalClick={onTotalClick}
                  onTransitionClick={onTransitionClick}
                  getStateDisplayName={getStateDisplayName}
                  getStateColor={getStateColor}
                  hasBrandData={hasBrandData}
                />
              ))}
            {companySideStates.some(
              (state) => allStatesSet.has(state) && !userSideStates.includes(state),
            ) && (
              <tr>
                <td
                  className="bg-orange-50 text-left px-1 py-3 text-sm font-bold text-orange-800 uppercase tracking-wider border-b-2 border-orange-200 sticky left-0 z-5 bg-orange-50 border-r border-orange-200"
                  style={{ minWidth: "120px", width: "120px" }}
                >
                  Firma Tarafı
                </td>
                <td
                  colSpan={statsData.length * 4}
                  className="bg-orange-50 border-b-2 border-orange-200"
                ></td>
              </tr>
            )}
            {companySideStates
              .filter((state) => allStatesSet.has(state) && !userSideStates.includes(state))
              .map((state) => (
                <StatsTableRow
                  key={state}
                  state={state}
                  statsData={statsData}
                  viewMode="weekly"
                  onTotalClick={onTotalClick}
                  onTransitionClick={onTransitionClick}
                  getStateDisplayName={getStateDisplayName}
                  getStateColor={getStateColor}
                  hasBrandData={hasBrandData}
                />
              ))}
            {Array.from(allStatesSet).filter(
              (state) => !userSideStates.includes(state) && !companySideStates.includes(state),
            ).length > 0 && (
              <tr>
                <td
                  className="bg-gray-50 text-left px-1 py-3 text-sm font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 sticky left-0 z-5 bg-gray-50 border-r border-gray-200"
                  style={{ minWidth: "120px", width: "120px" }}
                >
                  Diğer
                </td>
                <td
                  colSpan={statsData.length * 4}
                  className="bg-gray-50 border-b-2 border-gray-200"
                ></td>
              </tr>
            )}
            {Array.from(allStatesSet)
              .filter(
                (state) => !userSideStates.includes(state) && !companySideStates.includes(state),
              )
              .sort()
              .map((state) => (
                <StatsTableRow
                  key={state}
                  state={state}
                  statsData={statsData}
                  viewMode="weekly"
                  onTotalClick={onTotalClick}
                  onTransitionClick={onTransitionClick}
                  getStateDisplayName={getStateDisplayName}
                  getStateColor={getStateColor}
                  hasBrandData={hasBrandData}
                />
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default WeeklyStatsTable
